import "../../../../common/scss/table.scss";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../../content/ssiUplineReport";
import { useLeafVolumeLabel } from "../../../../common/leaf/LeafVolumeLabel/LeafVolumeLabel";
import DonutProgressCircle from "../../DonutProgressCard/DonutProgressCards";
import { getLongPeriodFormat } from "../../../../common/util/period";
import { useAppDispatch } from "../../../../store";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { IconCarrot } from "../../../../common/icons/IconCarrot/IconCarrot";
import { Row } from "../OutOfProgramWrapper/OutOfProgramWrapper";
import { useEffect, useRef } from "react";
import { openProfileModal } from "../../../../reducers/ssiUplineReport";
import { track } from "../../../../common/util/tealium";
import TEALIUM from "../../../../common/enums/tealium-enums";
import { storedLocale } from "../../../Authentication/util";
import "../../Shared/SsiUplineTable.scss";

type Props = {
  rows: Row[];
  isLoading: boolean;
  selectedAboNumber: string;
};

const OutOfProgramTable = ({ rows, isLoading, selectedAboNumber }: Props) => {
  const { t } = useTranslation(["ssiUplineReport"]);
  const getVolumeLabel = useLeafVolumeLabel();
  const scrollRef = useRef<HTMLTableRowElement>(null);
  const dispatch = useAppDispatch();
  const locale = storedLocale();

  const formatDate = (period: string) => {
    if (!period) return;

    return getLongPeriodFormat(period, locale);
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selectedAboNumber]);

  const LoadingRow = () => (
    <tr className="row">
      <td>
        <div className="aboNameNum">
          <LeafRectangleLoader width="8rem" height="1rem" margin="0 0 0.25rem 0" isLoading={true} />
          <LeafRectangleLoader width="4rem" height="0.75rem" margin="0" isLoading={true} />
        </div>
      </td>
      <td>
        <LeafRectangleLoader width="6rem" height="1rem" margin="0" isLoading={true} />
      </td>
      <td>
        <LeafRectangleLoader width="6rem" height="1rem" margin="0" isLoading={true} />
      </td>
      <td className="TableBody--centered">
        <LeafRectangleLoader width="6rem" height="1rem" margin="0" isLoading={true} />
      </td>
      <td className="TableBody--centered">
        <LeafRectangleLoader width="6rem" height="1rem" margin="0" isLoading={true} />
      </td>
      <td>
        <div className="sponsorNameCell">
          <LeafRectangleLoader width="6rem" height="1rem" margin="0" isLoading={true} />
          <LeafRectangleLoader width="6rem" height="1rem" margin="0" isLoading={true} />
        </div>
      </td>
    </tr>
  );

  if (isLoading) {
    return (
      <div className="SSIUplineTable">
        <table className="Table Table--rounded Table--noFirstChildPadding">
          <thead>
            <tr>
              <th>
                <LeafRectangleLoader width="8rem" height="1rem" margin="0" isLoading={true} />
              </th>
              <th>
                <LeafRectangleLoader width="6rem" height="1rem" margin="0" isLoading={true} />
              </th>
              <th>
                <LeafRectangleLoader width="6rem" height="1rem" margin="0" isLoading={true} />
              </th>
              <th className="centerTh">
                <LeafRectangleLoader width="8rem" height="1rem" margin="0" isLoading={true} />
              </th>
              <th className="centerTh">
                <LeafRectangleLoader width="8rem" height="1rem" margin="0" isLoading={true} />
              </th>
              <th>
                <LeafRectangleLoader width="6rem" height="1rem" margin="0" isLoading={true} />
              </th>
            </tr>
          </thead>
          <tbody className="TableBody TableBody--oneRemPaddingForFirstChildThenNoLeftPadding TableBody--rowBorder">
            <LoadingRow />
            <LoadingRow />
            <LoadingRow />
          </tbody>
        </table>
      </div>
    );
  }

  if (!isLoading && (!rows || (rows && rows?.length === 0))) return <></>;

  const requirements = rows[0]?.Requirements;

  return (
    <div className="SSIUplineTable">
      <table className="Table Table--rounded Table--noFirstChildPadding">
        <thead>
          <tr>
            <th>{t("inSSIProgram.columns.aboNameNum", defaultContent.inSSIProgram.columns.aboNameNum).toString()}</th>
            {requirements?.VolumeRequirements.length > 0 && (
              <th>
                {rows[0].Aff === "430"
                  ? `${getVolumeLabel("member")} / ${getVolumeLabel("vcs")}`
                  : getVolumeLabel(requirements.VolumeRequirements[0].Name)}
              </th>
            )}
            {requirements?.PercentRequirements && requirements.PercentRequirements.length > 0 && (
              <th>{getVolumeLabel(requirements.PercentRequirements[0].Name)}</th>
            )}
            <th className="centerTh">
              {t(
                "inSSIProgram.columns.programStartDate",
                defaultContent.inSSIProgram.columns.programStartDate,
              ).toString()}
            </th>
            <th className="centerTh">
              {t(
                "inSSIProgram.columns.monthsInProgram",
                defaultContent.inSSIProgram.columns.monthsInProgram,
              ).toString()}
            </th>
            <th>{t("inSSIProgram.columns.sponsor", defaultContent.inSSIProgram.columns.sponsor).toString()}</th>
          </tr>
        </thead>
        <tbody className="TableBody TableBody--oneRemPaddingForFirstChildThenNoLeftPadding TableBody--rowBorder">
          {rows.map((row, ind) => (
            <tr
              key={ind}
              className={`row ${row.IsHighlighted ? "row__highlighted" : ""}`}
              aria-selected={row.IsHighlighted}
              ref={row.scrollRef ? scrollRef : null}
            >
              <td
                aria-label={row.AboName}
                className={`${row.IsHighlighted ? "highlightLeftmostCell" : ""} ${
                  ind === rows.length - 1 ? "lastRowBorderLeft" : ""
                }`}
              >
                <div className="aboNameNum">
                  <span className="name">{row.AboName}</span>
                  <span className="num">#{row.AboNum}</span>
                </div>
              </td>
              {row.Requirements.VolumeRequirements.length > 0 &&
                row.Requirements.VolumeRequirements.map((volReq, i) => (
                  <td key={`volReq${i}`}>
                    <DonutProgressCircle
                      label={volReq.Name}
                      ariaTitle={getVolumeLabel(volReq.Name)}
                      currentAmount={volReq.Volume.Current.Actual}
                      targetAmount={volReq.Volume.Target.Actual}
                      met={volReq.Met}
                    />
                  </td>
                ))}
              {row.Requirements.PercentRequirements &&
                row.Requirements.PercentRequirements.length > 0 &&
                row.Requirements.PercentRequirements.map((percentReq, i) => (
                  <td key={`percReq${i}`}>
                    <DonutProgressCircle
                      label={percentReq.Name}
                      ariaTitle={getVolumeLabel(percentReq.Name)}
                      currentAmount={percentReq.Percent.Current.Actual}
                      targetAmount={percentReq.Percent.Target.Actual}
                      percent={true}
                      met={percentReq.Met}
                    />
                  </td>
                ))}
              <td className="TableBody--centered">
                {formatDate(row.EligibilityWindow.StartPeriod || row.EligibilityWindow.EntryPeriod)}
              </td>
              <td className={`TableBody--centered`}>
                {row.EligibilityWindow.MarketHasGracePeriod ? (
                  <>
                    <span className="TableBody--bold">{row.EligibilityWindow.AchievedMonths}</span>/
                    {row.EligibilityWindow.AchievedMonthsWithGrace}
                  </>
                ) : (
                  <>{row.EligibilityWindow.AchievedMonths}</>
                )}
              </td>
              <td
                aria-label={row.SponsorName}
                className={`${row.IsHighlighted ? "highlightRightmostCell" : ""} ${
                  ind === rows.length - 1 ? "lastRowBorderRight" : ""
                }`}
              >
                <div className="sponsorNameCell">
                  <span className="name">{row.SponsorName}</span>
                  <button
                    id={`OutOfProgramTableProfileButton${row.AboNum}`}
                    onClick={() => {
                      dispatch(
                        openProfileModal({
                          aboNo: row.AboNum,
                          affNo: row.Aff,
                          sponsorNo: row.SponsorNum,
                          sponsorAff: row.Aff,
                          eligibilityWindow: row.EligibilityWindow,
                          showEligibilityWindow: true,
                          show: true,
                          previousElementID: `OutOfProgramTableProfileButton${row.AboNum}`,
                        }),
                      );
                      track(TEALIUM.U_TAG_METHOD.LINK, "", "in-window", "SSI_Upline_Report", "", "ABO_profile");
                    }}
                    className="button"
                  >
                    <div className="buttonContents">
                      <div>
                        <span className="profileText">{t("profile", defaultContent.profile as string)}</span>
                        <div className="underline"></div>
                      </div>
                      <IconCarrot orientation="right" color="black" />
                    </div>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default OutOfProgramTable;
